import { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import Papa from 'papaparse';
import { DialogView, Button, Notification, InputField } from "./index";
import { FileInputField } from './FileInputField';
import { enqueuePutawayToLocations, getPutawayToLocationsStatus } from '../services';

const REQUIRED_COLUMNS = ['sku', 'warehouse_location_code', 'quantity'];

const COLUMN_ALIASES = {
  cubbo: 'warehouse_location_code',
  ubicacion: 'warehouse_location_code',
  ubicación: 'warehouse_location_code',
  location: 'warehouse_location_code',
  lote: 'lot_code',
  lot: 'lot_code',
  vencimiento: 'expiration_date',
  expiration: 'expiration_date',
  fecha_vencimiento: 'expiration_date',
  'suma de cant.': 'quantity',
  cantidad: 'quantity',
  qty: 'quantity',
};

const CSV_TEMPLATE_HEADER = 'sku,warehouse_location_code,quantity,lot_code,expiration_date';
const CSV_TEMPLATE_EXAMPLES = [
  'SKU-001,A-01-01-1,10,,',
  'SKU-002,A-01-01-3,5,BATCH-2028,2028-06-30',
];

const POLLING_INTERVAL_MS = 2500;

const STATUS = {
  IDLE: 'IDLE',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
};

/**
 * Attempts to parse a date string in DD/MM/YY or DD/MM/YYYY format
 * and converts it to YYYY-MM-DD. Returns the original string if
 * it's already in ISO format or null if the value is empty.
 */
const normalizeDate = (value) => {
  if (!value || !value.trim()) return null;
  const trimmed = value.trim();

  // Already ISO format (YYYY-MM-DD)
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;

  // DD/MM/YY or DD/MM/YYYY or D/M/YY or D/M/YYYY
  const match = trimmed.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})$/);
  if (match) {
    const day = match[1].padStart(2, '0');
    const month = match[2].padStart(2, '0');
    let year = match[3];
    if (year.length === 2) {
      const num = parseInt(year, 10);
      year = num >= 0 && num <= 50 ? `20${year}` : `19${year}`;
    }
    return `${year}-${month}-${day}`;
  }

  return trimmed;
};

const resolveHeader = (raw) => {
  const normalized = raw.trim().toLowerCase();
  return COLUMN_ALIASES[normalized] || normalized;
};

export const SimulatePutawayModal = ({ open, setOpen }) => {
  const { t } = useTranslation();
  const pollingRef = useRef(null);
  const timeoutRef = useRef(null);

  const [replenishmentId, setReplenishmentId] = useState('');
  const [parsedData, setParsedData] = useState([]);
  const [parseErrors, setParseErrors] = useState([]);
  const [fileName, setFileName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [taskStatus, setTaskStatus] = useState(STATUS.IDLE);
  const [progressPercentage, setProgressPercentage] = useState(0);
  const [taskErrors, setTaskErrors] = useState([]);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorNotificationMessage, setErrorNotificationMessage] = useState('');
  const [hasLotData, setHasLotData] = useState(false);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  const resetState = useCallback(() => {
    setParsedData([]);
    setParseErrors([]);
    setFileName('');
    setError(null);
    setReplenishmentId('');
    setTaskStatus(STATUS.IDLE);
    setProgressPercentage(0);
    setTaskErrors([]);
    setShowSuccessNotification(false);
    setShowErrorNotification(false);
    setErrorNotificationMessage('');
    setHasLotData(false);
    stopPolling();
  }, [stopPolling]);

  const handleClose = () => {
    if (!loading && taskStatus !== STATUS.IN_PROGRESS) {
      setOpen(false);
      resetState();
    }
  };

  const validateRow = (row, index) => {
    const errors = [];
    if (!row.sku || !row.sku.trim()) {
      errors.push(t('simulate_putaway.validation.missing_sku', { row: index + 1 }));
    }
    if (!row.warehouse_location_code || !row.warehouse_location_code.trim()) {
      errors.push(t('simulate_putaway.validation.missing_location', { row: index + 1 }));
    }
    if (!row.quantity || isNaN(parseInt(row.quantity)) || parseInt(row.quantity) <= 0) {
      errors.push(t('simulate_putaway.validation.invalid_quantity', { row: index + 1 }));
    }
    if (row.expiration_date && row.expiration_date.trim()) {
      const normalized = normalizeDate(row.expiration_date);
      if (normalized && !/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
        errors.push(t('simulate_putaway.validation.invalid_expiration_date', { row: index + 1 }));
      }
    }
    return errors;
  };

  const handleFileUpload = (files) => {
    if (!files || files.length === 0) return;

    const file = files[0];
    setFileName(file.name);
    setError(null);
    setParseErrors([]);
    setTaskStatus(STATUS.IDLE);
    setTaskErrors([]);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: resolveHeader,
      complete: (results) => {
        const headers = results.meta.fields || [];
        const missingColumns = REQUIRED_COLUMNS.filter(col => !headers.includes(col));

        if (missingColumns.length > 0) {
          setError(t('simulate_putaway.validation.missing_columns', {
            columns: missingColumns.join(', ')
          }));
          setParsedData([]);
          return;
        }

        const allErrors = [];
        const allWarnings = [];
        const validRows = [];
        let foundLotData = false;
        let skippedLotCount = 0;

        results.data.forEach((row, index) => {
          const rowErrors = validateRow(row, index);
          if (rowErrors.length > 0) {
            allErrors.push(...rowErrors);
          } else {
            const parsedRow = {
              sku: row.sku.trim(),
              warehouse_location_code: row.warehouse_location_code.trim(),
              quantity: parseInt(row.quantity)
            };

            const lotCode = row.lot_code ? row.lot_code.trim() : null;
            const expirationDate = row.expiration_date ? normalizeDate(row.expiration_date) : null;

            if (lotCode && lotCode !== '0' && lotCode.toLowerCase() !== '(en blanco)') {
              parsedRow.lot_code = lotCode;
              foundLotData = true;
              if (expirationDate) {
                parsedRow.expiration_date = expirationDate;
              } else {
                allWarnings.push(t('simulate_putaway.validation.lot_without_expiration', { row: index + 1, lot_code: lotCode }));
              }
            } else if (lotCode) {
              skippedLotCount++;
            }

            validRows.push(parsedRow);
          }
        });

        if (skippedLotCount > 0) {
          allWarnings.push(t('simulate_putaway.validation.skipped_empty_lots', { count: skippedLotCount }));
        }

        if (allErrors.length > 0 || allWarnings.length > 0) {
          setParseErrors([...allErrors, ...allWarnings].slice(0, 15));
        }

        setHasLotData(foundLotData);
        setParsedData(validRows);
      },
      error: (err) => {
        setError(t('simulate_putaway.validation.parse_error', { message: err.message }));
      }
    });
  };

  const handleDownloadTemplate = () => {
    const csvContent = [CSV_TEMPLATE_HEADER, ...CSV_TEMPLATE_EXAMPLES].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'putaway_to_locations_template.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const startPolling = (jobId) => {
    stopPolling();
    pollingRef.current = setInterval(async () => {
      try {
        const result = await getPutawayToLocationsStatus(jobId);

        if (result.status === 'IN_PROGRESS') {
          setProgressPercentage(result.progress_percentage || 0);
          return;
        }

        if (result.status === 'COMPLETED') {
          stopPolling();
          setProgressPercentage(100);

          // Small delay so user sees 100% before state change
          timeoutRef.current = setTimeout(() => {
            timeoutRef.current = null;
            setLoading(false);
            if (result.success) {
              setTaskStatus(STATUS.COMPLETED);
              setShowSuccessNotification(true);
            } else {
              setTaskStatus(STATUS.FAILED);
              setTaskErrors(result.errors || []);
            }
          }, 500);
          return;
        }

        if (result.status === 'FAILED') {
          stopPolling();
          setLoading(false);
          setTaskStatus(STATUS.FAILED);
          setTaskErrors(result.errors || [{ type: 'job_failed', message: t('simulate_putaway.error_types.job_failed') }]);
          return;
        }
      } catch (err) {
        stopPolling();
        setLoading(false);
        setTaskStatus(STATUS.FAILED);
        setTaskErrors([{ type: 'unexpected_error', message: err.message }]);
      }
    }, POLLING_INTERVAL_MS);
  };

  const handleSubmit = async () => {
    if (parsedData.length === 0 || !replenishmentId) return;

    setLoading(true);
    setError(null);
    setTaskErrors([]);
    setTaskStatus(STATUS.IN_PROGRESS);
    setProgressPercentage(0);

    try {
      const { job_id } = await enqueuePutawayToLocations({
        replenishment_id: parseInt(replenishmentId),
        location_rows: parsedData
      });

      startPolling(job_id);
    } catch (err) {
      setLoading(false);
      setTaskStatus(STATUS.IDLE);

      if (err.response?.status === 409) {
        setError(t('simulate_putaway.error_conflict'));
      } else if (err.response?.status === 404) {
        setError(t('simulate_putaway.error_not_found'));
      } else {
        const message = err.response?.data?.error || err.response?.data?.message || t('simulate_putaway.error_generic');
        setErrorNotificationMessage(message);
        setShowErrorNotification(true);
      }
    }
  };

  const canSubmit = parsedData.length > 0 && replenishmentId && taskStatus !== STATUS.IN_PROGRESS;

  return (
    <>
      <Notification
        show={showSuccessNotification}
        setShow={setShowSuccessNotification}
        type="success"
        title={t('simulate_putaway.success_title')}
        description={t('simulate_putaway.success_description', { count: parsedData.length })}
      />
      <Notification
        show={showErrorNotification}
        setShow={setShowErrorNotification}
        type="error"
        title={t('simulate_putaway.error_title')}
        description={errorNotificationMessage}
      />
      <DialogView open={open} setOpen={handleClose} allowOverflow={true}>
        <div style={{ width: '75vh', minWidth: '75vh', maxHeight: '85vh', overflowY: 'auto' }}>
          <div className="mt-3 text-center sm:mt-5">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              {t('simulate_putaway.title')}
            </h3>
            <p className="mt-2 text-sm text-gray-500">
              {t('simulate_putaway.description')}
            </p>
          </div>

          <div className="mt-6 space-y-4">
            {/* Instructions */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-blue-900 mb-2">
                {t('simulate_putaway.instructions_title')}
              </h4>
              <ul className="text-sm text-blue-800 space-y-1 list-disc ml-4">
                <li>{t('simulate_putaway.instruction_1')}</li>
                <li>{t('simulate_putaway.instruction_2')}</li>
                <li>{t('simulate_putaway.instruction_3')}</li>
                <li>{t('simulate_putaway.instruction_4')}</li>
                <li>{t('simulate_putaway.instruction_5')}</li>
              </ul>
              <div className="mt-3">
                <Button
                  type="secondary"
                  onClick={handleDownloadTemplate}
                  dense
                >
                  {t('simulate_putaway.download_template')}
                </Button>
              </div>
            </div>

            {/* Replenishment ID */}
            <InputField
              label={t('simulate_putaway.replenishment_id_label')}
              type="number"
              value={replenishmentId}
              onChange={(value) => setReplenishmentId(value)}
              placeholder={t('simulate_putaway.replenishment_id_placeholder')}
              required
            />

            {/* File Upload */}
            <FileInputField
              label={t('simulate_putaway.file_label')}
              onChange={handleFileUpload}
              accepts={["text/csv", ".csv"]}
              multiple={false}
              maxFiles={1}
              placeholder={fileName || t('simulate_putaway.file_placeholder')}
              help={t('simulate_putaway.file_help')}
              maxFileSize={10000000}
            />

            {/* Parse Errors */}
            {parseErrors.length > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                <h4 className="text-sm font-medium text-yellow-800 mb-1">
                  {t('simulate_putaway.warnings_title')}
                </h4>
                <ul className="text-xs text-yellow-700 space-y-1 list-disc ml-4">
                  {parseErrors.map((err, idx) => (
                    <li key={idx}>{err}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* General Error */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* Progress Bar */}
            {taskStatus === STATUS.IN_PROGRESS && (
              <div className="bg-indigo-50 border border-indigo-200 rounded-md p-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-indigo-700">
                    {t('simulate_putaway.progress_label')}
                  </span>
                  <span className="text-sm font-semibold text-indigo-700">
                    {progressPercentage}%
                  </span>
                </div>
                <div className="w-full bg-indigo-200 rounded-full h-2.5">
                  <div
                    className="bg-indigo-600 h-2.5 rounded-full transition-all duration-300"
                    style={{ width: `${progressPercentage}%` }}
                  />
                </div>
              </div>
            )}

            {/* Task Completed Successfully */}
            {taskStatus === STATUS.COMPLETED && (
              <div className="bg-green-50 border border-green-200 rounded-md p-4">
                <div className="flex items-center">
                  <svg className="h-5 w-5 text-green-400 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm font-medium text-green-800">
                    {t('simulate_putaway.success_description', { count: parsedData.length })}
                  </span>
                </div>
              </div>
            )}

            {/* Task Errors */}
            {taskStatus === STATUS.FAILED && taskErrors.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <h4 className="text-sm font-medium text-red-800 mb-2">
                  {t('simulate_putaway.task_errors_title')}
                </h4>
                <div className="max-h-60 overflow-y-auto space-y-2">
                  {taskErrors.map((err, idx) => (
                    <div key={idx} className="border border-red-200 rounded p-2 bg-white">
                      <span className="text-xs font-semibold text-red-800 block">
                        {t(`simulate_putaway.error_types.${err.type}`, { defaultValue: err.type })}
                      </span>
                      <span className="text-xs text-red-700">{err.message}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Preview Table */}
            {parsedData.length > 0 && taskStatus === STATUS.IDLE && (
              <div className="border border-gray-200 rounded-md overflow-hidden">
                <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                  <span className="text-sm font-medium text-gray-700">
                    {t('simulate_putaway.preview_title', { count: parsedData.length })}
                  </span>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('simulate_putaway.column_location')}</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('simulate_putaway.column_quantity')}</th>
                        {hasLotData && (
                          <>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('simulate_putaway.column_lot_code')}</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('simulate_putaway.column_expiration_date')}</th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {parsedData.map((row, idx) => (
                        <tr key={idx}>
                          <td className="px-4 py-2 text-sm text-gray-900">{row.sku}</td>
                          <td className="px-4 py-2 text-sm text-gray-900">{row.warehouse_location_code}</td>
                          <td className="px-4 py-2 text-sm text-gray-900">{row.quantity}</td>
                          {hasLotData && (
                            <>
                              <td className="px-4 py-2 text-sm text-gray-500">{row.lot_code || '-'}</td>
                              <td className="px-4 py-2 text-sm text-gray-500">{row.expiration_date || '-'}</td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Buttons */}
          <div className="flex justify-end space-x-3 pt-6">
            <Button
              type="secondary"
              onClick={handleClose}
              disabled={loading || taskStatus === STATUS.IN_PROGRESS}
            >
              {taskStatus === STATUS.COMPLETED || taskStatus === STATUS.FAILED
                ? t('simulate_putaway.buttons.close')
                : t('simulate_putaway.buttons.cancel')
              }
            </Button>
            {taskStatus !== STATUS.COMPLETED && (
              <Button
                type="primary"
                onClick={handleSubmit}
                loading={loading}
                loading_label={t('simulate_putaway.buttons.processing')}
                disabled={!canSubmit}
              >
                {t('simulate_putaway.buttons.confirm', { count: parsedData.length })}
              </Button>
            )}
          </div>
        </div>
      </DialogView>
    </>
  );
};
